import React, { useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import apiClient from '../lib/api';
import './LegalCharts.css';
import { RefreshCw, BarChart3, PieChart as PieChartIcon } from 'lucide-react';

interface ActStats {
  act: string;
  count: number;
  percentage: number;
}

interface SectionStats {
  section: string;
  count: number;
  percentage: number;
}

interface LegalChartsProps {
  isMainPageHeader?: boolean;
  searchInput?: string;
  setSearchInput?: (value: string) => void;
  filterOpen?: boolean;
  setFilterOpen?: (value: boolean) => void;
  dateFilter?: string;
  setDateFilter?: (value: string) => void;
  sortBy?: string;
  setSortBy?: (value: string) => void;
}

const LegalCharts: React.FC<LegalChartsProps> = ({ 
  isMainPageHeader = false,
  searchInput = '',
  setSearchInput,
  filterOpen = false,
  setFilterOpen,
  dateFilter = '',
  setDateFilter,
  sortBy = 'decision_date',
  setSortBy
}) => {
  const [actsData, setActsData] = useState<ActStats[]>([]);
  const [sectionsData, setSectionsData] = useState<SectionStats[]>([]);
  const [selectedAct, setSelectedAct] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [chartType, setChartType] = useState<'pie' | 'bar'>('pie');

  // Color palette for charts - original vibrant colors
  const COLORS = [
    '#BADA55', '#8BC34A', '#4CAF50', '#2196F3', '#FF9800',
    '#F44336', '#9C27B0', '#607D8B', '#795548', '#FFC107',
    '#00BCD4', '#E91E63', '#3F51B5', '#009688', '#FF5722'
  ];

  const processActsDataForChart = (data: ActStats[], threshold: number = 2) => {
    const processed = data.filter(item => item.percentage >= threshold);
    const others = data.filter(item => item.percentage < threshold);
    
    if (others.length > 0) {
      const othersTotal = others.reduce((sum, item) => sum + item.count, 0);
      const othersPercentage = others.reduce((sum, item) => sum + item.percentage, 0);
      
      processed.push({
        act: 'Others',
        count: othersTotal,
        percentage: othersPercentage
      });
    }
    
    return processed;
  };

  const processSectionsDataForChart = (data: SectionStats[], threshold: number = 3) => {
    const processed = data.filter(item => item.percentage >= threshold);
    const others = data.filter(item => item.percentage < threshold);
    
    if (others.length > 0) {
      const othersTotal = others.reduce((sum, item) => sum + item.count, 0);
      const othersPercentage = others.reduce((sum, item) => sum + item.percentage, 0);
      
      processed.push({
        section: 'Others',
        count: othersTotal,
        percentage: othersPercentage
      });
    }
    
    return processed;
  };

  const fetchActsData = useCallback(async () => {
    try {
      const data = await apiClient.legal.getActsStats();
      const processedData = processActsDataForChart(data, 2); // Show only items with 2% or more
      setActsData(processedData);
      if (!selectedAct && data.length > 0) {
        setSelectedAct(data[0].act);
      }
    } catch (error) {
      console.error('Error fetching acts data:', error);
    }
  }, [selectedAct]);

  const fetchSectionsData = useCallback(async (actName: string) => {
    try {
      const data = await apiClient.legal.getSectionsStats(actName);
      const processedData = processSectionsDataForChart(data, 3); // Show only sections with 3% or more
      setSectionsData(processedData);
    } catch (error) {
      console.error('Error fetching sections data:', error);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchActsData();
      setLoading(false);
    };

    fetchData();
  }, [fetchActsData]);

  useEffect(() => {
    if (selectedAct) {
      fetchSectionsData(selectedAct);
    }
  }, [selectedAct, fetchSectionsData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchActsData();
      if (selectedAct) {
        fetchSectionsData(selectedAct);
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, selectedAct, fetchActsData, fetchSectionsData]);

  const handleActClick = (data: ActStats) => {
    setSelectedAct(data.act);
  };

  const handleActSelect = (actName: string) => {
    setSelectedAct(actName);
  };

  const formatTooltip = (value: number, name: string, props: any) => {
    const payload = props.payload;
    if (payload) {
      const label = payload.act || payload.section;
      if (label) {
        return [
          `${label}: ${value} cases (${payload.percentage.toFixed(1)}%)`,
          ''
        ];
      }
    }
    return [`${value} cases`, 'Count'];
  };

  const formatLabel = (entry: any) => {
    return ''; // Remove percentage labels from pie slices
  };

  if (loading) {
    return (
      <div className="legal-charts-container">
        <div className="charts-loading">
          <RefreshCw className="spinning" size={24} />
          <p>Loading precedents statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`legal-charts-container ${isMainPageHeader ? 'main-page-header' : ''}`}>
      {isMainPageHeader && (
        <div className="charts-header main-header">
          <div className="charts-title">
            <h3>Precedents Case Distribution</h3>
            <p>Real-time analysis of OLT cases by Act and Section</p>
          </div>
          
          <div className="unified-controls">
            <div className="search-filter-section">
              <div className="search-control">
                <input 
                  id="precedents-search" 
                  placeholder="Search case numbers, decisions..."
                  value={searchInput} 
                  onChange={(e) => setSearchInput && setSearchInput(e.target.value)} 
                  className="header-search"
                />
              </div>
              <div className="filter-control">
                <button className="filter-toggle" onClick={() => setFilterOpen && setFilterOpen(!filterOpen)}>
                  {filterOpen ? 'Filter ▴' : 'Filter ▾'}
                </button>
              </div>
            </div>
            
            <div className="chart-controls">
              <div className="chart-type-toggle">
                <button 
                  className={`chart-type-btn ${chartType === 'pie' ? 'active' : ''}`}
                  onClick={() => setChartType('pie')}
                >
                  <PieChartIcon size={16} />
                  Pie
                </button>
                <button 
                  className={`chart-type-btn ${chartType === 'bar' ? 'active' : ''}`}
                  onClick={() => setChartType('bar')}
                >
                  <BarChart3 size={16} />
                  Bar
                </button>
              </div>
              <button 
                className={`refresh-btn ${autoRefresh ? 'active' : ''}`}
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                <RefreshCw size={16} />
                {autoRefresh ? 'Auto' : 'Manual'}
              </button>
              <button 
                className="refresh-btn manual"
                onClick={() => {
                  fetchActsData();
                  if (selectedAct) fetchSectionsData(selectedAct);
                }}
              >
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>
          
          {filterOpen && (
            <div className="filter-dropdown">
              <div className="filter-dropdown-content">
                <div className="filter-row">
                  <div className="filter-group">
                    <select value={dateFilter} onChange={(e) => setDateFilter && setDateFilter(e.target.value)}>
                      <option>All Dates</option>
                      <option>This Week</option>
                      <option>This Month</option>
                      <option>This Quarter</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <select value={sortBy} onChange={(e) => setSortBy && setSortBy(e.target.value)}>
                      <option value="decision_date">Decision Date (Newest)</option>
                      <option value="case_number">Case Number</option>
                      <option value="title">Title (A-Z)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="charts-grid">
        {/* Acts Distribution Chart */}
        <div className="chart-container">
          <div className="chart-header">
            <h4>Cases by Act</h4>
            <span className="total-cases">{actsData.reduce((sum, item) => sum + item.count, 0)} total cases</span>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={300}>
              {chartType === 'pie' ? (
                <PieChart>
                  <Pie
                    data={actsData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={formatLabel}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                    onClick={handleActClick}
                    className="clickable-slice"
                  >
                    {actsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={formatTooltip} />
                  <Legend 
                    formatter={(value, entry) => {
                      const data = entry?.payload as any;
                      return data?.act || value;
                    }}
                  />
                </PieChart>
              ) : (
                <BarChart data={actsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="act" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    fontSize={12}
                  />
                  <YAxis />
                  <Tooltip formatter={formatTooltip} />
                  <Bar dataKey="count" fill="#BADA55" />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Sections Distribution Chart */}
            <div className="chart-container">
              <div className="chart-header">
                <h4>Sections by Act</h4>
                <select 
                  value={selectedAct} 
                  onChange={(e) => handleActSelect(e.target.value)}
                  className="act-selector"
                >
                  <option value="">Select an Act...</option>
                  <option value="Planning Act">Planning Act (823 cases)</option>
                  <option value="Expropriations Act">Expropriations Act (227 cases)</option>
                  <option value="Development Charges Act">Development Charges Act (44 cases)</option>
                  <option value="Ontario Heritage Act">Ontario Heritage Act (39 cases)</option>
                  <option value="City of Toronto Act">City of Toronto Act (24 cases)</option>
                  <option value="Environmental Protection Act">Environmental Protection Act (14 cases)</option>
                  <option value="Niagara Escarpment Planning & Development Act">Niagara Escarpment Planning & Development Act (13 cases)</option>
                  <option value="Conservation Authorities Act">Conservation Authorities Act (9 cases)</option>
                  <option value="Mining Act">Mining Act (5 cases)</option>
                  <option value="Municipal Act">Municipal Act (5 cases)</option>
                </select>
              </div>
          <div className="chart-content">
            {selectedAct ? (
              <ResponsiveContainer width="100%" height={300}>
                {chartType === 'pie' ? (
                  <PieChart>
                    <Pie
                      data={sectionsData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={formatLabel}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {sectionsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={formatTooltip} />
                    <Legend 
                      formatter={(value, entry) => {
                        const data = entry?.payload as any;
                        return data?.section || value;
                      }}
                    />
                  </PieChart>
                ) : (
                  <BarChart data={sectionsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="section" 
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      fontSize={12}
                    />
                    <YAxis />
                    <Tooltip formatter={formatTooltip} />
                    <Bar dataKey="count" fill="#4CAF50" />
                  </BarChart>
                )}
              </ResponsiveContainer>
            ) : (
              <div className="chart-placeholder">
                <p>👆 Click on an act above or select from the dropdown to view its sections</p>
                <p className="placeholder-subtitle">Each act has different precedents sections that are commonly challenged</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="charts-summary">
        <div className="summary-card">
          <h5>Top Act</h5>
          <div className="summary-value">
            {actsData[0]?.act || 'N/A'}
          </div>
          <div className="summary-percentage">
            {actsData[0]?.percentage || 0}% of all cases
          </div>
        </div>
        <div className="summary-card">
          <h5>Top Section</h5>
          <div className="summary-value">
            {sectionsData[0]?.section || 'N/A'}
          </div>
          <div className="summary-percentage">
            {sectionsData[0]?.percentage || 0}% of {selectedAct} cases
          </div>
        </div>
        <div className="summary-card">
          <h5>Total Acts</h5>
          <div className="summary-value">
            {actsData.length}
          </div>
          <div className="summary-percentage">
            Different precedents acts
          </div>
        </div>
        <div className="summary-card">
          <h5>Auto Refresh</h5>
          <div className="summary-value">
            {autoRefresh ? 'ON' : 'OFF'}
          </div>
          <div className="summary-percentage">
            Updates every 30s
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalCharts;
